// Fechas y recordatorios de la agenda. Las fechas llegan como "YYYY-MM-DD"
// (sin hora): se comparan como días del calendario local para que la zona
// horaria no corra el compromiso un día.

const MS_POR_DIA = 24 * 60 * 60 * 1000;

// días que avisa antes de la fecha (además de hoy, mañana y vencidos)
export const DIAS_AVISO_PREVIO = 3;

const aDiaLocal = (fechaTexto) => {
  const [anio, mes, dia] = fechaTexto.split("-").map(Number);
  return new Date(anio, mes - 1, dia);
};

const hoyLocal = () => {
  const ahora = new Date();
  return new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
};

// "YYYY-MM-DD" de hoy (mínimo sugerido en el campo de fecha)
export const fechaHoyTexto = () => {
  const hoy = hoyLocal();
  const dosDigitos = (n) => String(n).padStart(2, "0");
  return `${hoy.getFullYear()}-${dosDigitos(hoy.getMonth() + 1)}-${dosDigitos(hoy.getDate())}`;
};

// días desde hoy hasta la fecha (0 = hoy, 1 = mañana, negativo = vencido)
export const diasHasta = (fechaTexto) => Math.round((aDiaLocal(fechaTexto) - hoyLocal()) / MS_POR_DIA);

// "lunes, 20 de octubre de 2026"
export const formatearFecha = (fechaTexto) =>
  aDiaLocal(fechaTexto).toLocaleDateString("es-CO", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

// "25 oct 2026"
export const fechaCorta = (fechaTexto) =>
  aDiaLocal(fechaTexto)
    .toLocaleDateString("es-CO", { day: "numeric", month: "short", year: "numeric" })
    .replace(".", "");

// "15:30:00" -> "3:30 p. m."
export const formatearHora = (hora) => {
  if (!hora) return "";
  const [h, m] = hora.split(":").map(Number);
  return new Date(2000, 0, 1, h, m).toLocaleTimeString("es-CO", { hour: "numeric", minute: "2-digit" });
};

// "15:30:00" -> "15:30" (valor del input type="time")
export const horaParaInput = (hora) => (hora ? hora.slice(0, 5) : "");

// fecha y hora local del compromiso (null si no tiene fecha y hora)
export const momentoCompromiso = (compromiso) => {
  if (!compromiso.fecha || !compromiso.hora) return null;
  const [h, m] = compromiso.hora.split(":").map(Number);
  const momento = aDiaLocal(compromiso.fecha);
  momento.setHours(h, m, 0, 0);
  return momento;
};

// "Juan Pérez" y/o "Líder: Ana" del compromiso, para mostrar con quién es
export const relacionesCompromiso = (compromiso) =>
  [
    compromiso.Elector && {
      icono: "mdi:account-outline",
      texto: `${compromiso.Elector.nombres} ${compromiso.Elector.apellidos}`,
      titulo: `Votante · CC ${compromiso.Elector.cedula}`,
    },
    compromiso.lider && { icono: "mdi:account-star-outline", texto: compromiso.lider, titulo: "Líder" },
  ].filter(Boolean);

// Estado del recordatorio de un compromiso. tipo: cumplido | sinFecha |
// vencido | hoy | manana | proximo (dentro de DIAS_AVISO_PREVIO) | futuro
export const recordatorio = (compromiso) => {
  if (compromiso.cumplido) return { tipo: "cumplido", texto: "Cumplido", urgente: false };
  if (!compromiso.fecha) return { tipo: "sinFecha", texto: "Sin fecha", urgente: false };

  const dias = diasHasta(compromiso.fecha);
  const aLas = compromiso.hora ? ` a las ${formatearHora(compromiso.hora)}` : "";
  if (dias < 0) {
    const atraso = Math.abs(dias);
    return { tipo: "vencido", dias, texto: `Vencido hace ${atraso} ${atraso === 1 ? "día" : "días"}`, urgente: true };
  }
  if (dias === 0) return { tipo: "hoy", dias, texto: `Es hoy${aLas}`, urgente: true };
  if (dias === 1) return { tipo: "manana", dias, texto: `Es mañana${aLas}`, urgente: true };
  if (dias <= DIAS_AVISO_PREVIO) return { tipo: "proximo", dias, texto: `Faltan ${dias} días`, urgente: true };
  return { tipo: "futuro", dias, texto: `Faltan ${dias} días`, urgente: false };
};

// compromisos pendientes que requieren aviso, del más atrasado al más lejano
export const compromisosPorAvisar = (compromisos = []) =>
  compromisos
    .filter((compromiso) => recordatorio(compromiso).urgente)
    .sort((a, b) => diasHasta(a.fecha) - diasHasta(b.fecha));

const plural = (cantidad, singular, varios) => `${cantidad} ${cantidad === 1 ? singular : varios}`;

// resumen para el aviso al entrar: "Hoy tienes 1 compromiso · Mañana tienes 2 compromisos"
export const resumenAvisos = (porAvisar) => {
  const contar = (tipo) => porAvisar.filter((c) => recordatorio(c).tipo === tipo).length;
  const vencidos = contar("vencido");
  const hoy = contar("hoy");
  const manana = contar("manana");
  const proximos = contar("proximo");

  return [
    hoy && `Hoy tienes ${plural(hoy, "compromiso", "compromisos")}`,
    manana && `Mañana tienes ${plural(manana, "compromiso", "compromisos")}`,
    proximos && `${plural(proximos, "compromiso", "compromisos")} en los próximos ${DIAS_AVISO_PREVIO} días`,
    vencidos && `${plural(vencidos, "compromiso vencido", "compromisos vencidos")} sin cumplir`,
  ].filter(Boolean);
};

// filtros de la vista de agenda
export const FILTROS_AGENDA = [
  { valor: "pendientes", etiqueta: "Pendientes", cumple: (c) => !c.cumplido },
  { valor: "votantes", etiqueta: "Con votantes", cumple: (c) => esDeVotante(c) && !c.cumplido },
  { valor: "cumplidos", etiqueta: "Cumplidos", cumple: (c) => c.cumplido },
  { valor: "todos", etiqueta: "Todos", cumple: () => true },
];

// compromiso registrado con un votante (viene del registro, no de la agenda personal)
export const esDeVotante = (compromiso) => compromiso.origen === "votante";
